import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMigrationContractOptions,
  searchMigrationContracts,
  type ContratoCampanasMigracionFiltroRequest,
  type ContratoCampanasMigracionResponse,
  InternalSearchCandidateResponse,
  type MigrationContractOptionsResponse,
} from "@/shared/actions/campanas/migracion.actions";
import {
  getFidelizationContractOptions,
  searchFidelizationContracts,
  type ContratoCampanasFiltroRequest,
  type ContratoCampanasFiltroResponse,
  type FidelizationContractOptionsResponse,
} from "@/shared/actions/campanas/fidelizacion.actions";
type CampaignType = "MIGRATION" | "FIDELIZATION";

type FidelizationFiltersForm = {
  fechaDesde: string;
  planNombre: string;
  estadoContrato: string;
  formasPagoExcluidas: string[];
  ciudad: string;
  tipoPlan: string;
};

const initialFidelizationFilters: FidelizationFiltersForm = {
  fechaDesde: "",
  planNombre: "",
  estadoContrato: "",
  formasPagoExcluidas: [],
  ciudad: "",
  tipoPlan: "",
};

type MigrationFiltersForm = {
  ciudad: string;
  tipo_plan: string;
  tipo_servicio: string;
  estados_contrato: string[];
  formas_pago_excluidas: string[];
  observacion_instalado: string;
  observacion_excluida: string;
  modelos: string[];
};

const initialMigrationFilters: MigrationFiltersForm = {
  ciudad: "",
  tipo_plan: "",
  tipo_servicio: "",
  estados_contrato: [],
  formas_pago_excluidas: [],
  observacion_instalado: "",
  observacion_excluida: "",
  modelos: [],
};

const emptyMigrationOptions: MigrationContractOptionsResponse = {
  observaciones: [],
  modelos: [],
  ciudades: [],
  tiposPlan: [],
  tiposServicio: [],
  estadosContrato: [],
  formasPago: [],
};

const emptyFidelizationOptions: FidelizationContractOptionsResponse = {
  planes: [],
  ciudades: [],
  tiposPlan: [],
  estadosContrato: [],
  formasPago: [],
};

const normalizeFidelizationOptions = (
  response?:
    | FidelizationContractOptionsResponse
    | { data?: FidelizationContractOptionsResponse }
    | null,
) => {
  const data = (response as any)?.data ?? response ?? emptyFidelizationOptions;

  return {
    planes: Array.isArray(data.planes) ? data.planes : [],
    ciudades: Array.isArray(data.ciudades) ? data.ciudades : [],
    tiposPlan: Array.isArray(data.tiposPlan) ? data.tiposPlan : [],
    estadosContrato: Array.isArray(data.estadosContrato)
      ? data.estadosContrato
      : [],
    formasPago: Array.isArray(data.formasPago) ? data.formasPago : [],
  };
};

const normalizeMigrationOptions = (
  response?:
    | MigrationContractOptionsResponse
    | { data?: MigrationContractOptionsResponse }
    | null,
) => {
  const data = (response as any)?.data ?? response ?? emptyMigrationOptions;

  return {
    observaciones: Array.isArray(data.observaciones)
      ? data.observaciones
      : [],
    modelos: Array.isArray(data.modelos) ? data.modelos : [],
    ciudades: Array.isArray(data.ciudades) ? data.ciudades : [],
    tiposPlan: Array.isArray(data.tiposPlan) ? data.tiposPlan : [],
    tiposServicio: Array.isArray(data.tiposServicio)
      ? data.tiposServicio
      : [],
    estadosContrato: Array.isArray(data.estadosContrato)
      ? data.estadosContrato
      : [],
    formasPago: Array.isArray(data.formasPago) ? data.formasPago : [],
  };
};

const getSearchErrorMessage = (campaignType: CampaignType) => {
  return campaignType === "MIGRATION"
    ? "No se pudo ejecutar la búsqueda de migración. Inténtalo nuevamente o contacta a soporte si el problema continúa."
    : "No se pudo ejecutar la búsqueda de fidelización. Inténtalo nuevamente o contacta a soporte si el problema continúa.";
};

const getBackendErrorMessage = (error: any, campaignType: CampaignType) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.detail ||
    error?.message ||
    getSearchErrorMessage(campaignType)
  );
};

const isEmptyText = (value: string) => !value.trim();

interface InternalSearchCandidatesProps {
  campaignType: CampaignType;
  onSearchSuccess: (response: InternalSearchCandidateResponse) => void;
  onFidelizationFiltersChange?: (
    filters: ContratoCampanasFiltroRequest | null,
  ) => void;
  onFidelizationResultsChange?: (
    results: ContratoCampanasFiltroResponse[],
  ) => void;
  onMigrationResultsChange?: (
    results: ContratoCampanasMigracionResponse[],
  ) => void;
  onMigrationFiltersChange?: (
    filters: ContratoCampanasMigracionFiltroRequest | null,
  ) => void;
}

export const InternalSearchCandidates = ({
  campaignType,
  onSearchSuccess,
  onFidelizationFiltersChange,
  onFidelizationResultsChange,
  onMigrationResultsChange,
  onMigrationFiltersChange,
}: InternalSearchCandidatesProps) => {
  const [migrationFilters, setMigrationFilters] = useState(
    initialMigrationFilters,
  );
  const [fidelizationFilters, setFidelizationFilters] = useState(
    initialFidelizationFilters,
  );
  const [error, setError] = useState("");
  const [lastResult, setLastResult] =
    useState<InternalSearchCandidateResponse | null>(null);
  const [fidelizationResults, setFidelizationResults] = useState<
    ContratoCampanasFiltroResponse[]
  >([]);
  const [migrationResults, setMigrationResults] = useState<
    ContratoCampanasMigracionResponse[]
  >([]);
  const [loadingFidelization, setLoadingFidelization] = useState(false);
  const [loadingMigration, setLoadingMigration] = useState(false);

  const queryClient = useQueryClient();
  const fidelizationOptionsQuery = useQuery({
    queryKey: ["fidelization-contract-options"],
    queryFn: getFidelizationContractOptions,
    enabled: campaignType === "FIDELIZATION",
    refetchOnWindowFocus: false,
    retry: false,
  });
  const migrationOptionsQuery = useQuery({
    queryKey: ["migration-contract-options"],
    queryFn: getMigrationContractOptions,
    enabled: campaignType === "MIGRATION",
    refetchOnWindowFocus: false,
    retry: false,
  });

  const migrationOptions = normalizeMigrationOptions(
    migrationOptionsQuery.data,
  );
  const fidelizationOptions = normalizeFidelizationOptions(
    fidelizationOptionsQuery.data,
  );
  const loadingFidelizationOptions =
    campaignType === "FIDELIZATION" &&
    (fidelizationOptionsQuery.isLoading || fidelizationOptionsQuery.isFetching);
  const loadingMigrationOptions =
    campaignType === "MIGRATION" &&
    (migrationOptionsQuery.isLoading || migrationOptionsQuery.isFetching);
  const isSearching = loadingFidelization || loadingMigration;
  const fidelizationControlsDisabled =
    isSearching || loadingFidelizationOptions;
  const migrationControlsDisabled = isSearching || loadingMigrationOptions;

  const campaignLabel =
    campaignType === "MIGRATION" ? "Migración" : "Fidelización";

  const getContractId = (contract: ContratoCampanasFiltroResponse) => {
    return (contract as any).idContrato ?? (contract as any).id_contrato ?? "";
  };

  const getCustomerName = (contract: ContratoCampanasFiltroResponse) => {
    return (
      (contract as any).razonSocial ??
      (contract as any).razon_social ??
      "-"
    );
  };

  const fidelizationResultRows = fidelizationResults.map((contract, index) => {
    const idContrato = getContractId(contract);
    const ciudad =
      (contract as any).ciudad ??
      (contract as any).city ??
      fidelizationFilters.ciudad;

    return {
      id: idContrato || `fidelization-result-${index}`,
      razonSocial: getCustomerName(contract),
      ciudad: ciudad || "-",
      idContrato: idContrato || "-",
      tipoPlan: fidelizationFilters.tipoPlan || "-",
      tickets: contract.tickets ?? 0,
    };
  });

  const fidelizationResultColumns: GridColDef[] = [
    {
      field: "razonSocial",
      headerName: "Razón social",
      flex: 1.4,
      minWidth: 180,
      sortable: false,
      filterable: true,
    },
    {
      field: "tipoPlan",
      headerName: "Tipo plan",
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      filterable: false,
    },
    {
      field: "ciudad",
      headerName: "Ciudad",
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      filterable: true,
    },
    {
      field: "tickets",
      headerName: "Tickets",
      type: "number",
      flex: 0.5,
      minWidth: 90,
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
    },
  ];

  const migrationResultRows = migrationResults.map((contract) => ({
    id: contract.candidateId,
    nombreCliente: contract.nombreCliente || "-",
    idContrato: contract.idContrato || "-",
    estadoContrato: contract.estadoContrato || "-",
    modelo: contract.modelo || "-",
    fechaEquipoAntiguo: contract.fechaEquipoAntiguo || "-",
    tickets: contract.tickets ?? 0,
    ciudad: contract.ciudad || "-",
  }));

  const migrationResultColumns: GridColDef[] = [
    {
      field: "nombreCliente",
      headerName: "Razón social",
      flex: 1.4,
      minWidth: 180,
      sortable: false,
      filterable: true,
    },
    {
      field: "estadoContrato",
      headerName: "Estado contrato",
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
    },
    {
      field: "modelo",
      headerName: "Modelo",
      flex: 1.3,
      minWidth: 180,
      sortable: false,
      filterable: false,
    },
    {
      field: "tickets",
      headerName: "Tickets",
      type: "number",
      flex: 0.5,
      minWidth: 90,
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
    },
    {
      field: "ciudad",
      headerName: "Ciudad",
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      filterable: true,
    },
  ];

  const buildFidelizationFilters = (): ContratoCampanasFiltroRequest => {
    return {
      fechaDesde: fidelizationFilters.fechaDesde,
      fecha_desde: fidelizationFilters.fechaDesde,
      ...(fidelizationFilters.planNombre
        ? {
            planNombre: fidelizationFilters.planNombre,
            plan_nombre: fidelizationFilters.planNombre,
          }
        : {}),
      ...(fidelizationFilters.estadoContrato
        ? {
            estadoContrato: fidelizationFilters.estadoContrato,
            estado_contrato: fidelizationFilters.estadoContrato,
          }
        : {}),
      ...(fidelizationFilters.formasPagoExcluidas.length > 0
        ? {
            formasPagoExcluidas:
              fidelizationFilters.formasPagoExcluidas,
            formas_pago_excluidas:
              fidelizationFilters.formasPagoExcluidas,
          }
        : {}),
      ...(fidelizationFilters.ciudad
        ? { ciudad: fidelizationFilters.ciudad }
        : {}),
      ...(fidelizationFilters.tipoPlan
        ? {
            tipoPlan: fidelizationFilters.tipoPlan,
            tipo_plan: fidelizationFilters.tipoPlan,
          }
        : {}),
    };
  };

  const buildMigrationFilters = (): ContratoCampanasMigracionFiltroRequest => {
    return {
      ...(migrationFilters.ciudad ? { ciudad: migrationFilters.ciudad } : {}),
      ...(migrationFilters.tipo_plan
        ? { tipo_plan: migrationFilters.tipo_plan }
        : {}),
      ...(migrationFilters.tipo_servicio
        ? { tipo_servicio: migrationFilters.tipo_servicio }
        : {}),
      ...(migrationFilters.estados_contrato.length > 0
        ? { estados_contrato: migrationFilters.estados_contrato }
        : {}),
      ...(migrationFilters.formas_pago_excluidas.length > 0
        ? {
            formas_pago_excluidas:
              migrationFilters.formas_pago_excluidas,
          }
        : {}),
      ...(migrationFilters.observacion_instalado
        ? { observacion_instalado: migrationFilters.observacion_instalado }
        : {}),
      ...(migrationFilters.observacion_excluida
        ? { observacion_excluida: migrationFilters.observacion_excluida }
        : {}),
      ...(migrationFilters.modelos.length > 0
        ? { modelos: migrationFilters.modelos }
        : {}),
    };
  };

  const updateFidelizationFilter = (
    field: keyof FidelizationFiltersForm,
    value: string | string[],
  ) => {
    setFidelizationFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateMigrationFilter = (
    field: keyof MigrationFiltersForm,
    value: string | string[],
  ) => {
    setMigrationFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = () => {
    if (campaignType === "FIDELIZATION") {
      const hasEmptyRequiredField =
        isEmptyText(fidelizationFilters.fechaDesde) ||
        isEmptyText(fidelizationFilters.planNombre) ||
        isEmptyText(fidelizationFilters.estadoContrato) ||
        isEmptyText(fidelizationFilters.ciudad) ||
        isEmptyText(fidelizationFilters.tipoPlan);

      if (hasEmptyRequiredField) {
        setError("Completa todos los filtros de fidelización para buscar.");
        return false;
      }

      setError("");
      return true;
    }

    const hasEmptyRequiredField =
      isEmptyText(migrationFilters.ciudad) ||
      isEmptyText(migrationFilters.tipo_plan) ||
      isEmptyText(migrationFilters.tipo_servicio) ||
      migrationFilters.estados_contrato.length === 0 ||
      isEmptyText(migrationFilters.observacion_instalado) ||
      migrationFilters.modelos.length === 0;

    if (hasEmptyRequiredField) {
      setError("Completa todos los filtros de migración para buscar.");
      return false;
    }

    setError("");
    return true;
  };

  const renderFidelizationOptionField = (
    field: keyof Pick<
      FidelizationFiltersForm,
      "planNombre" | "estadoContrato" | "ciudad" | "tipoPlan"
    >,
    label: string,
    options: string[],
    required = true,
  ) => (
    <Autocomplete
      options={options}
      value={(fidelizationFilters[field] as string) || null}
      onChange={(_, value) => updateFidelizationFilter(field, value ?? "")}
      loading={loadingFidelizationOptions}
      disabled={fidelizationControlsDisabled}
      noOptionsText="Sin opciones disponibles"
      loadingText="Cargando opciones..."
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          required={required}
          helperText={required ? " " : "Opcional"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingFidelizationOptions && (
                  <CircularProgress color="inherit" size={18} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const renderFidelizationMultiOptionField = (
    field: keyof Pick<FidelizationFiltersForm, "formasPagoExcluidas">,
    label: string,
    options: string[],
    required = true,
  ) => (
    <Autocomplete
      multiple
      options={options}
      value={fidelizationFilters[field] as string[]}
      onChange={(_, value) => updateFidelizationFilter(field, value)}
      loading={loadingFidelizationOptions}
      disabled={fidelizationControlsDisabled}
      noOptionsText="Sin opciones disponibles"
      loadingText="Cargando opciones..."
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          required={required}
          helperText={required ? " " : "Opcional"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingFidelizationOptions && (
                  <CircularProgress color="inherit" size={18} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const renderMigrationOptionField = (
    field: keyof Pick<
      MigrationFiltersForm,
      | "ciudad"
      | "tipo_plan"
      | "tipo_servicio"
      | "observacion_instalado"
      | "observacion_excluida"
    >,
    label: string,
    options: string[],
    required = true,
  ) => (
    <Autocomplete
      options={options}
      value={(migrationFilters[field] as string) || null}
      onChange={(_, value) => updateMigrationFilter(field, value ?? "")}
      loading={loadingMigrationOptions}
      disabled={migrationControlsDisabled}
      noOptionsText="Sin opciones disponibles"
      loadingText="Cargando opciones..."
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          required={required}
          helperText={required ? " " : "Opcional"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingMigrationOptions && (
                  <CircularProgress color="inherit" size={18} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const renderMigrationMultiOptionField = (
    field: keyof Pick<
      MigrationFiltersForm,
      "estados_contrato" | "formas_pago_excluidas" | "modelos"
    >,
    label: string,
    options: string[],
    required = true,
  ) => (
    <Autocomplete
      multiple
      options={options}
      value={migrationFilters[field] as string[]}
      onChange={(_, value) => updateMigrationFilter(field, value)}
      loading={loadingMigrationOptions}
      disabled={migrationControlsDisabled}
      noOptionsText="Sin opciones disponibles"
      loadingText="Cargando opciones..."
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          required={required}
          helperText={required ? " " : "Opcional"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingMigrationOptions && (
                  <CircularProgress color="inherit" size={18} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const handleSearch = () => {
    if (!validate()) return;

    const filters =
      campaignType === "FIDELIZATION" ? buildFidelizationFilters() : null;
    const migrationPayload =
      campaignType === "MIGRATION" ? buildMigrationFilters() : null;

    if (campaignType === "MIGRATION" && migrationPayload) {
      setLoadingMigration(true);
      setError("");
      setMigrationResults([]);

      searchMigrationContracts(migrationPayload)
        .then((response) => {
          const contracts = ((response as any)?.data ??
            response ??
            []) as ContratoCampanasMigracionResponse[];

          setMigrationResults(contracts);
          onMigrationFiltersChange?.(migrationPayload);
          onMigrationResultsChange?.(contracts);
          queryClient.invalidateQueries({
            queryKey: ["obsolete-candidates"],
          });
          queryClient.invalidateQueries({
            queryKey: [
              "available-ticket-candidates",
              campaignType,
              "INTERNAL_SEARCH",
            ],
          });

          const result: InternalSearchCandidateResponse = {
            module: campaignType,
            totalContractsFound: contracts.length,
            savedCandidates: contracts.length,
            ignoredWithoutDate: 0,
            message: "Búsqueda de migración procesada correctamente",
          };

          setLastResult(result);
          onSearchSuccess(result);
        })
        .catch((error: any) => {
          console.error("Error en búsqueda interna de migración", error);
          setMigrationResults([]);
          onMigrationFiltersChange?.(null);
          onMigrationResultsChange?.([]);
          setError(getBackendErrorMessage(error, campaignType));
        })
        .finally(() => {
          setLoadingMigration(false);
        });

      return;
    }

    if (campaignType === "FIDELIZATION" && filters) {
      setLoadingFidelization(true);
      setError("");
      setFidelizationResults([]);

      searchFidelizationContracts(filters)
        .then((response) => {
          const contracts = ((response as any)?.data ??
            response ??
            []) as ContratoCampanasFiltroResponse[];

          setFidelizationResults(contracts);
          onFidelizationFiltersChange?.(filters);
          onFidelizationResultsChange?.(contracts);
          queryClient.invalidateQueries({
            queryKey: ["fidelization-candidates"],
          });
          queryClient.invalidateQueries({
            queryKey: [
              "available-ticket-candidates",
              campaignType,
              "INTERNAL_SEARCH",
            ],
          });

          const result: InternalSearchCandidateResponse = {
            module: campaignType,
            totalContractsFound: contracts.length,
            savedCandidates: contracts.length,
            ignoredWithoutDate: 0,
            message: "Búsqueda de fidelización procesada correctamente",
          };

          setLastResult(result);
          onSearchSuccess(result);
        })
        .catch((error: any) => {
          console.error("Error en búsqueda interna de fidelización", error);
          setFidelizationResults([]);
          onFidelizationFiltersChange?.(null);
          onFidelizationResultsChange?.([]);
          setError(getBackendErrorMessage(error, campaignType));
        })
        .finally(() => {
          setLoadingFidelization(false);
        });

      return;
    }

    return;
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 980 },
        borderRadius: 3,
        boxShadow: 3,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Búsqueda interna - {campaignLabel}
            </Typography>

            <Typography variant="body1" color="text.secondary" mt={1}>
              {campaignType === "FIDELIZATION"
                ? "Completa los filtros para buscar contratos de fidelización."
                : "Completa los filtros para buscar contratos de migración."}
            </Typography>
          </Box>

          {error && <Alert severity="warning">{error}</Alert>}

          {campaignType === "FIDELIZATION" &&
            fidelizationOptionsQuery.isError && (
              <Alert severity="warning">
                No se pudieron cargar las opciones de fidelización. Intenta
                nuevamente en unos segundos.
              </Alert>
            )}

          {campaignType === "MIGRATION" && migrationOptionsQuery.isError && (
            <Alert severity="warning">
              No se pudieron cargar las opciones de migración. Intenta
              nuevamente en unos segundos.
            </Alert>
          )}

          {lastResult && campaignType === "FIDELIZATION" && (
            <Alert severity={lastResult.totalContractsFound > 0 ? "success" : "info"}>
              Contratos encontrados: {lastResult.totalContractsFound}
            </Alert>
          )}

          {lastResult && campaignType === "MIGRATION" && (
            <Alert severity={lastResult.totalContractsFound > 0 ? "success" : "info"}>
              Contratos encontrados: {lastResult.totalContractsFound}
            </Alert>
          )}

          {campaignType === "FIDELIZATION" ? (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              }}
              gap={2}
            >
              <TextField
                label="Fecha desde"
                type="date"
                value={fidelizationFilters.fechaDesde}
                onChange={(e) =>
                  setFidelizationFilters((prev) => ({
                    ...prev,
                    fechaDesde: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                helperText=" "
              />

              {renderFidelizationOptionField(
                "planNombre",
                "Plan",
                fidelizationOptions.planes,
              )}

              {renderFidelizationOptionField(
                "estadoContrato",
                "Estado contrato",
                fidelizationOptions.estadosContrato,
              )}

              {renderFidelizationMultiOptionField(
                "formasPagoExcluidas",
                "Formas de pago excluidas",
                fidelizationOptions.formasPago,
                false,
              )}

              {renderFidelizationOptionField(
                "ciudad",
                "Ciudad",
                fidelizationOptions.ciudades,
              )}

              {renderFidelizationOptionField(
                "tipoPlan",
                "Tipo plan",
                fidelizationOptions.tiposPlan,
              )}
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              }}
              gap={2}
            >
              {renderMigrationOptionField(
                "ciudad",
                "Ciudad",
                migrationOptions.ciudades,
              )}

              {renderMigrationOptionField(
                "tipo_plan",
                "Tipo plan",
                migrationOptions.tiposPlan,
              )}

              {renderMigrationOptionField(
                "tipo_servicio",
                "Tipo servicio",
                migrationOptions.tiposServicio,
              )}

              {renderMigrationMultiOptionField(
                "estados_contrato",
                "Estados contrato",
                migrationOptions.estadosContrato,
              )}

              {renderMigrationMultiOptionField(
                "formas_pago_excluidas",
                "Formas de pago excluidas",
                migrationOptions.formasPago,
                false,
              )}

              {renderMigrationOptionField(
                "observacion_instalado",
                "Observación instalado",
                migrationOptions.observaciones,
              )}

              {renderMigrationOptionField(
                "observacion_excluida",
                "Observación excluida",
                migrationOptions.observaciones,
                false,
              )}

              {renderMigrationMultiOptionField(
                "modelos",
                "Modelos",
                migrationOptions.modelos,
              )}
            </Box>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching}
              sx={{ borderRadius: 2, px: 4 }}
            >
              {isSearching ? "Buscando..." : "Buscar"}
            </Button>

            <Button
              variant="outlined"
              disabled={isSearching}
              onClick={() => {
                setMigrationFilters(initialMigrationFilters);
                setMigrationResults([]);
                setFidelizationFilters(initialFidelizationFilters);
                setFidelizationResults([]);
                setError("");
                setLastResult(null);
                onFidelizationFiltersChange?.(null);
                onFidelizationResultsChange?.([]);
                onMigrationFiltersChange?.(null);
                onMigrationResultsChange?.([]);
                onSearchSuccess({
                  module: campaignType,
                  totalContractsFound: 0,
                  savedCandidates: 0,
                  ignoredWithoutDate: 0,
                  message: "",
                });
              }}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Limpiar
            </Button>
          </Box>

          {campaignType === "MIGRATION" &&
            migrationResults.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} mb={1}>
                  Resultados de búsqueda
                </Typography>

                <Box sx={{ height: 300, width: "100%" }}>
                  <DataGrid
                    rows={migrationResultRows}
                    columns={migrationResultColumns}
                    disableColumnSelector
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    initialState={{
                      pagination: {
                        paginationModel: {
                          page: 0,
                          pageSize: 5,
                        },
                      },
                    }}
                    pageSizeOptions={[5, 10]}
                    localeText={{
                      noRowsLabel: "No hay resultados de búsqueda",
                    }}
                    sx={{
                      borderColor: "divider",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "grey.50",
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

          {campaignType === "FIDELIZATION" &&
            fidelizationResults.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} mb={1}>
                  Resultados de búsqueda
                </Typography>

                <Box sx={{ height: 300, width: "100%" }}>
                  <DataGrid
                    rows={fidelizationResultRows}
                    columns={fidelizationResultColumns}
                    disableColumnSelector
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    initialState={{
                      pagination: {
                        paginationModel: {
                          page: 0,
                          pageSize: 5,
                        },
                      },
                    }}
                    pageSizeOptions={[5, 10]}
                    localeText={{
                      noRowsLabel: "No hay resultados de búsqueda",
                    }}
                    sx={{
                      borderColor: "divider",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "grey.50",
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
        </Stack>
      </CardContent>
    </Card>
  );
};
